import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, Send } from "lucide-react";
import { Calendar } from "./ui/calendar";
import {
  NORA_GREETING,
  QUICK_ACTIONS,
  FALLBACK_ACTIONS,
  ANSWERS,
  FALLBACK_TEXT,
  detectIntent,
  APPOINTMENT_TOPICS,
  APPOINTMENT_TIMES,
  CALLBACK_TIMES,
  BROCHURE_TYPES,
  PROJECT_OBJECT_TYPES,
  PROJECT_SCOPES,
  PROJECT_STYLES,
  PROJECT_PACKAGES,
  SHOP_USAGE_TYPES,
} from "../data/noraConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLOW_STEPS = {
  appointment: ["topic", "date", "time", "name", "contact", "confirm"],
  callback: ["preferred_time", "name", "contact", "topic", "confirm"],
  brochure: ["brochure_type", "name", "email", "company_object", "confirm"],
  shop_set: ["set_name", "usage_type", "quantity_region", "timeline", "name", "contact", "confirm"],
  project: ["object_type", "project_scope", "units", "style_series", "package_level", "timeline", "name", "contact", "confirm"],
};

// Contextual follow-ups (max 3) per intent — never re-show the full home grid.
const CONTEXT_FOLLOWUPS = {
  default: [
    { label: "Projekt konfigurieren", flow: "project" },
    { label: "Shop ansehen", to: "/shop" },
    { label: "Termin anfragen", flow: "appointment" },
  ],
  shop: [
    { label: "Zum Shop", to: "/shop" },
    { label: "Termin anfragen", flow: "appointment" },
  ],
  configurator: [
    { label: "Zum Konfigurator", to: "/konfigurator" },
    { label: "Termin anfragen", flow: "appointment" },
  ],
  refit: [
    { label: "Refit ansehen", to: "/refit" },
    { label: "Projekt konfigurieren", flow: "project" },
    { label: "Termin anfragen", flow: "appointment" },
  ],
  styles: [
    { label: "Stilserien ansehen", to: "/stilserien" },
    { label: "Projekt konfigurieren", flow: "project" },
  ],
  packages: [
    { label: "Konzepte ansehen", to: "/konzepte" },
    { label: "Projekt konfigurieren", flow: "project" },
  ],
};

const followupsFor = (intent) => {
  switch (intent) {
    case "explain_shop":
    case "shop_set_request":
      return CONTEXT_FOLLOWUPS.shop;
    case "explain_configurator":
      return CONTEXT_FOLLOWUPS.configurator;
    case "explain_refit":
      return CONTEXT_FOLLOWUPS.refit;
    case "explain_style_series":
      return CONTEXT_FOLLOWUPS.styles;
    case "explain_packages":
      return CONTEXT_FOLLOWUPS.packages;
    default:
      return CONTEXT_FOLLOWUPS.default;
  }
};

export default function Nora() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [started, setStarted] = useState(false); // becomes true after first user action
  const [thinking, setThinking] = useState(false);
  const [flow, setFlow] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({});
  const [input, setInput] = useState("");
  const [submitState, setSubmitState] = useState("idle");
  const [submitted, setSubmitted] = useState(null);
  const [showMoreQA, setShowMoreQA] = useState(false);
  const streamRef = useRef(null);
  const bottomRef = useRef(null);
  const thinkingTimer = useRef(null);

  // Greeting on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "nora", kind: "text", text: NORA_GREETING }]);
    }
  }, [open, messages.length]);

  // Auto-scroll to newest content (bottom anchor)
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, thinking, flow, stepIdx, submitState]);

  // Body scroll lock on mobile
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    if (window.innerWidth < 1024) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Cleanup pending thinking timer
  useEffect(() => () => clearTimeout(thinkingTimer.current), []);

  // Allow external triggers (e.g. mobile Smoked Glass Dock) to open Nora
  useEffect(() => {
    const onOpenRequest = () => setOpen(true);
    window.addEventListener("livinora:open-nora", onOpenRequest);
    return () => window.removeEventListener("livinora:open-nora", onOpenRequest);
  }, []);

  // Broadcast open state so the dock can hide itself when Nora is visible
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("livinora:nora-state", { detail: { open } })
    );
  }, [open]);

  // ---- Helpers -----------------------------------------------------------
  const reset = () => {
    setFlow(null);
    setStepIdx(0);
    setData({});
    setSubmitState("idle");
    setSubmitted(null);
  };

  const restart = () => {
    reset();
    setStarted(false);
    setShowMoreQA(false);
    setMessages([{ role: "nora", kind: "text", text: NORA_GREETING }]);
  };

  const pushUser = (text) => setMessages((m) => [...m, { role: "user", kind: "text", text }]);
  const pushNora = (entry) => setMessages((m) => [...m, { role: "nora", ...entry }]);

  /**
   * Show the "Nora denkt nach"-bubble, then run `then()` after `ms`.
   * Disables further input during the wait. Single-instance — never stacks.
   */
  const respond = (then, ms = 600) => {
    if (thinking) return;
    setThinking(true);
    clearTimeout(thinkingTimer.current);
    thinkingTimer.current = setTimeout(() => {
      then();
      setThinking(false);
    }, ms);
  };

  // ---- Actions -----------------------------------------------------------
  const startFlow = (flowKey) => {
    setStarted(true);
    reset();
    setFlow(flowKey);
    setStepIdx(0);
    respond(() => pushNora({ kind: "text", text: flowIntro(flowKey) }), 550);
  };

  const handleQuickAction = (qa) => {
    if (thinking) return;
    pushUser(qa.label);
    setStarted(true);
    if (qa.flow === "explain") {
      handleExplain(qa.topic);
    } else {
      startFlow(qa.flow);
    }
  };

  const handleExplain = (topic) => {
    let key = "explain_livinora";
    if (topic === "refit") key = "explain_refit";
    if (topic === "diff") key = "explain_diff";
    if (topic === "package") key = "explain_packages";

    respond(() => {
      if (key === "explain_diff") {
        pushNora({ kind: "text", text: ANSWERS.explain_shop });
        pushNora({ kind: "text", text: ANSWERS.explain_configurator });
        pushNora({ kind: "actions", actions: CONTEXT_FOLLOWUPS.default });
      } else {
        pushNora({ kind: "text", text: ANSWERS[key] || FALLBACK_TEXT });
        pushNora({ kind: "actions", actions: followupsFor(key) });
      }
    }, key === "explain_diff" ? 850 : 700);
  };

  const handleSendInput = () => {
    if (thinking) return;
    const text = input.trim();
    if (!text) return;
    pushUser(text);
    setInput("");
    setStarted(true);
    const intent = detectIntent(text);
    const intentToFlow = {
      appointment_request: "appointment",
      callback_request: "callback",
      brochure_request: "brochure",
      shop_set_request: "shop_set",
      project_request: "project",
    };
    if (intentToFlow[intent]) {
      startFlow(intentToFlow[intent]);
      return;
    }
    if (ANSWERS[intent]) {
      respond(() => {
        pushNora({ kind: "text", text: ANSWERS[intent] });
        // Skip followup chips for the short greeting — let the conversation breathe.
        if (intent !== "greeting") {
          pushNora({ kind: "actions", actions: followupsFor(intent) });
        }
      }, 700);
      return;
    }
    respond(() => {
      pushNora({ kind: "text", text: FALLBACK_TEXT });
      pushNora({ kind: "quick_actions", actions: FALLBACK_ACTIONS });
    }, 600);
  };

  const advance = (label, patch) => {
    if (thinking) return;
    if (label) pushUser(label);
    setData((d) => ({ ...d, ...(patch || {}) }));
    respond(() => setStepIdx((i) => i + 1), 500);
  };

  const submitFlow = async () => {
    if (thinking) return;
    setSubmitState("sending");
    setThinking(true);
    const typeMap = {
      appointment: "appointment_request",
      callback: "callback_request",
      brochure: "brochure_request",
      shop_set: "shop_set_request",
      project: "project_request",
    };
    const payload = {
      type: typeMap[flow],
      name: data.name || null,
      email: data.email || null,
      phone: data.phone || null,
      message: data.message || null,
      data: stripContact(data),
    };
    try {
      const res = await fetch(`${API}/nora/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Bad status");
      const body = await res.json();
      // Min thinking duration so the success feels considered, not abrupt.
      const elapsed = Date.now() - performance.timeOrigin;
      const wait = elapsed < 800 ? 800 - elapsed : 0;
      setTimeout(() => {
        setSubmitted(body);
        setSubmitState("success");
        setThinking(false);
      }, Math.min(wait, 200) + 600);
    } catch (err) {
      setSubmitState("error");
      setThinking(false);
    }
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const triggerLifted = pathname.startsWith("/shop/") && pathname !== "/shop";
  const hideTrigger = pathname === "/kontakt";

  return (
    <>
      {!hideTrigger && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="nora-trigger"
          aria-label="Nora — Livinora Concierge öffnen"
          className={`hidden lg:inline-flex fixed right-4 sm:right-6 z-[55] items-center gap-3 pl-4 pr-5 py-3.5 bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] border border-[color:var(--livinora-ink)] shadow-[0_18px_44px_-18px_rgba(0,0,0,0.45)] transition-transform duration-500 hover:-translate-y-0.5 ${
            triggerLifted ? "bottom-24 lg:bottom-6" : "bottom-6"
          }`}
        >
          <span className="w-7 h-7 inline-flex items-center justify-center rounded-full bg-[color:var(--livinora-paper)] text-[color:var(--livinora-ink)] serif text-sm">
            N
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[12px] uppercase tracking-[0.2em]">Nora fragen</span>
            <span className="text-[9px] uppercase tracking-[0.22em] text-[color:var(--livinora-paper)]/65">
              Livinora Concierge
            </span>
          </span>
        </button>
      )}

      {open && (
        <NoraPanel
          onClose={() => setOpen(false)}
          messages={messages}
          started={started}
          thinking={thinking}
          flow={flow}
          stepIdx={stepIdx}
          data={data}
          submitState={submitState}
          submitted={submitted}
          input={input}
          setInput={setInput}
          handleSendInput={handleSendInput}
          handleQuickAction={handleQuickAction}
          startFlow={startFlow}
          advance={advance}
          submitFlow={submitFlow}
          restart={restart}
          navigate={navigate}
          streamRef={streamRef}
          bottomRef={bottomRef}
          isMobile={isMobile}
          showMoreQA={showMoreQA}
          setShowMoreQA={setShowMoreQA}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
function flowIntro(flowKey) {
  switch (flowKey) {
    case "appointment":
      return "Gerne. Worum geht es hauptsächlich?";
    case "callback":
      return "Gerne. Wann passt Ihnen ein Rückruf am besten?";
    case "brochure":
      return "Gerne. Welche Unterlagen interessieren Sie?";
    case "shop_set":
      return "Die Livinora Raumsets sind vorkonfiguriert. Statt einer Bestellung bereiten wir eine Anfrage vor — welcher Raum oder welches Set interessiert Sie?";
    case "project":
      return "Einige kurze Fragen, damit wir Ihr Projekt sauber einordnen können. Um welche Objektart geht es?";
    default:
      return "";
  }
}

function stripContact(d) {
  const out = { ...d };
  ["name", "email", "phone", "message"].forEach((k) => delete out[k]);
  return out;
}

function fmtDate(d) {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

// ---------------------------------------------------------------------------
function NoraPanel(props) {
  const {
    onClose, messages, started, thinking, flow, stepIdx, data,
    submitState, submitted, input, setInput, handleSendInput, handleQuickAction,
    startFlow, advance, submitFlow, restart, navigate, streamRef, bottomRef,
    isMobile, showMoreQA, setShowMoreQA,
  } = props;

  // Stable layout: header (shrink-0) | stream (flex-1, scrollable) | composer (shrink-0)
  const containerCls = isMobile
    ? "fixed inset-0 z-[60] flex flex-col bg-[color:var(--livinora-paper)]"
    : "fixed bottom-6 right-6 z-[60] w-[420px] h-[640px] max-h-[80vh] flex flex-col bg-[color:var(--livinora-paper)] border border-[color:var(--livinora-border)] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.45)]";

  const QA_PRIMARY = QUICK_ACTIONS.slice(0, 5);
  const QA_MORE = QUICK_ACTIONS.slice(5);

  return (
    <div className={containerCls} role="dialog" aria-modal="true" aria-label="Nora Concierge" data-testid="nora-panel">
      {/* Header (fixed) */}
      <div className="shrink-0 px-5 sm:px-6 py-4 border-b border-[color:var(--livinora-border)] flex items-center justify-between bg-[color:var(--livinora-paper)]">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] serif text-base">
            N
          </span>
          <div className="leading-tight">
            <div className="serif text-lg">Nora</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--livinora-muted)]">
              Livinora Concierge
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Nora schließen"
          data-testid="nora-close"
          className="w-9 h-9 inline-flex items-center justify-center text-[color:var(--livinora-ink)] hover:opacity-70 transition-opacity"
        >
          <X size={18} />
        </button>
      </div>

      {/* Stream (only this part scrolls) */}
      <div
        ref={streamRef}
        className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 pt-5 pb-6 space-y-3.5"
        data-testid="nora-stream"
      >
        {messages.map((m, i) => (
          <Bubble key={i} msg={m} navigate={navigate} startFlow={startFlow} handleQuickAction={handleQuickAction} thinking={thinking} />
        ))}

        {/* Initial quick actions — only on first interaction, never afterwards */}
        {!started && !flow && submitState === "idle" && (
          <div className="space-y-2 pt-1" data-testid="nora-quick-actions">
            {(showMoreQA ? QUICK_ACTIONS : QA_PRIMARY).map((qa) => (
              <button
                key={qa.id}
                type="button"
                disabled={thinking}
                onClick={() => handleQuickAction(qa)}
                data-testid={`nora-qa-${qa.id}`}
                className="w-full text-left text-[12px] uppercase tracking-[0.16em] px-4 py-3 min-h-[44px] border border-[color:var(--livinora-border)] hover:border-[color:var(--livinora-ink)] hover:bg-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-paper)] transition-colors disabled:opacity-50"
              >
                {qa.label}
              </button>
            ))}
            {QA_MORE.length > 0 && (
              <button
                type="button"
                onClick={() => setShowMoreQA((v) => !v)}
                data-testid="nora-qa-more"
                className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--livinora-muted)] hover:text-[color:var(--livinora-ink)] underline-offset-4 hover:underline pt-1"
              >
                {showMoreQA ? "Weniger anzeigen" : "Weitere Optionen"}
              </button>
            )}
          </div>
        )}

        {/* Active flow step */}
        {flow && submitState === "idle" && !thinking && (
          <FlowStep
            flow={flow}
            step={FLOW_STEPS[flow][stepIdx]}
            data={data}
            advance={advance}
            submitFlow={submitFlow}
            thinking={thinking}
          />
        )}

        {/* Thinking bubble */}
        {thinking && <ThinkingBubble />}

        {submitState === "success" && submitted && (
          <SuccessCard submitted={submitted} data={data} restart={restart} navigate={navigate} />
        )}

        {submitState === "error" && (
          <div className="hairline border bg-[color:var(--livinora-paper-warm)] p-5 text-sm">
            Ihre Anfrage konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.
            <button onClick={submitFlow} className="block mt-3 text-[color:var(--livinora-ink)] underline-offset-4 underline">
              Erneut senden
            </button>
          </div>
        )}

        <div ref={bottomRef} aria-hidden style={{ height: 1 }} />
      </div>

      {/* Composer (fixed) — input only when no active flow + idle */}
      {!flow && submitState === "idle" && (
        <div
          className="shrink-0 border-t border-[color:var(--livinora-border)] px-4 sm:px-5 py-3 flex items-center gap-2 bg-[color:var(--livinora-paper)]"
          style={isMobile ? { paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" } : undefined}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendInput()}
            disabled={thinking}
            data-testid="nora-input"
            placeholder="Frage an Nora …"
            className="flex-1 bg-transparent border border-[color:var(--livinora-border)] focus:border-[color:var(--livinora-ink)] outline-none px-3 h-10 text-sm disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleSendInput}
            disabled={thinking || !input.trim()}
            data-testid="nora-send"
            aria-label="Nachricht senden"
            className="w-10 h-10 inline-flex items-center justify-center bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] disabled:opacity-50"
          >
            <Send size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ThinkingBubble() {
  return (
    <div
      className="max-w-[85%] inline-flex items-center gap-3 text-sm leading-relaxed bg-[color:var(--livinora-paper-warm)] border border-[color:var(--livinora-border)] px-4 py-3"
      data-testid="nora-thinking"
      aria-live="polite"
    >
      <span className="text-[color:var(--livinora-muted)]">Nora bereitet die Antwort vor</span>
      <span className="inline-flex gap-1 ml-1">
        <Dot delay={0} />
        <Dot delay={160} />
        <Dot delay={320} />
      </span>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="block w-1.5 h-1.5 rounded-full bg-[color:var(--livinora-ink)]/70 animate-pulse"
      style={{ animationDelay: `${delay}ms`, animationDuration: "1100ms" }}
    />
  );
}

// ---------------------------------------------------------------------------
function Bubble({ msg, navigate, startFlow, handleQuickAction, thinking }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] text-sm bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] px-4 py-2.5 leading-snug">
          {msg.text}
        </div>
      </div>
    );
  }
  if (msg.kind === "text") {
    return (
      <div className="max-w-[85%] text-sm leading-relaxed text-[color:var(--livinora-ink)] bg-[color:var(--livinora-paper-warm)] border border-[color:var(--livinora-border)] px-4 py-3">
        {msg.text}
      </div>
    );
  }
  if (msg.kind === "actions") {
    return (
      <div className="flex flex-wrap gap-2 pt-0.5" data-testid="nora-followup-actions">
        {msg.actions.map((a, i) => (
          <button
            key={i}
            type="button"
            disabled={thinking}
            onClick={() => {
              if (a.to) navigate(a.to);
              else if (a.flow) startFlow(a.flow);
            }}
            className="text-[10.5px] uppercase tracking-[0.16em] px-3 py-2 min-h-[36px] border border-[color:var(--livinora-ink)] text-[color:var(--livinora-ink)] hover:bg-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-paper)] transition-colors disabled:opacity-50"
          >
            {a.label}
          </button>
        ))}
      </div>
    );
  }
  if (msg.kind === "quick_actions") {
    return (
      <div className="flex flex-wrap gap-2 pt-0.5">
        {msg.actions.map((a) => (
          <button
            key={a.id}
            type="button"
            disabled={thinking}
            onClick={() => handleQuickAction(a)}
            className="text-[10.5px] uppercase tracking-[0.16em] px-3 py-2 min-h-[36px] border border-[color:var(--livinora-ink)] text-[color:var(--livinora-ink)] hover:bg-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-paper)] transition-colors disabled:opacity-50"
          >
            {a.label}
          </button>
        ))}
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Compact, premium choice chips (replaces former full-width black blocks)
function ChoiceChips({ items, field, advance, columns = 0 }) {
  const cls = columns > 0
    ? `grid grid-cols-2 ${columns >= 4 ? "sm:grid-cols-4" : "sm:grid-cols-3"} gap-2`
    : "flex flex-wrap gap-2";
  return (
    <div className={cls} data-testid={`nora-chips-${field}`}>
      {items.map((it) => (
        <button
          key={it}
          type="button"
          onClick={() => advance(it, { [field]: it })}
          data-testid={`nora-opt-${field}-${it.replace(/\s+/g, "-").toLowerCase()}`}
          className="text-[11px] uppercase tracking-[0.14em] px-3 py-2.5 min-h-[40px] text-center border border-[color:var(--livinora-border)] text-[color:var(--livinora-ink)] hover:border-[color:var(--livinora-ink)] hover:bg-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-paper)] transition-colors"
        >
          {it}
        </button>
      ))}
    </div>
  );
}

function FlowStep({ flow, step, data, advance, submitFlow }) {
  // Appointment ----------------------------------------------------------
  if (flow === "appointment") {
    if (step === "topic") return <ChoiceChips items={APPOINTMENT_TOPICS} field="topic" advance={advance} />;
    if (step === "date")
      return (
        <div className="hairline border bg-[color:var(--livinora-paper-warm)] p-2 sm:p-3 max-w-full overflow-hidden" data-testid="nora-calendar">
          <DatePick onPick={(d) => advance(fmtDate(d), { selected_date: d?.toISOString() })} />
        </div>
      );
    if (step === "time") return <ChoiceChips items={APPOINTMENT_TIMES} field="selected_time" advance={advance} columns={4} />;
    if (step === "name") return <NameInput onSubmit={(name) => advance(name, { name })} />;
    if (step === "contact")
      return <ContactInputs onSubmit={(p) => advance(`${p.email} · ${p.phone || "—"}`, p)} extraField="object_type" extraLabel="Objektart / Anliegen" />;
    if (step === "confirm") return <ConfirmCard data={data} onSubmit={submitFlow} flow={flow} />;
  }
  // Callback -------------------------------------------------------------
  if (flow === "callback") {
    if (step === "preferred_time") return <ChoiceChips items={CALLBACK_TIMES} field="preferred_time" advance={advance} columns={3} />;
    if (step === "name") return <NameInput onSubmit={(name) => advance(name, { name })} />;
    if (step === "contact")
      return <ContactInputs onSubmit={(p) => advance(`${p.phone} · ${p.email || "—"}`, p)} requirePhone />;
    if (step === "topic") return <FreeText label="Anliegen" placeholder="Worum geht es?" onSubmit={(v) => advance(v, { topic: v, message: v })} />;
    if (step === "confirm") return <ConfirmCard data={data} onSubmit={submitFlow} flow={flow} />;
  }
  // Brochure -------------------------------------------------------------
  if (flow === "brochure") {
    if (step === "brochure_type") return <ChoiceChips items={BROCHURE_TYPES} field="brochure_type" advance={advance} />;
    if (step === "name") return <NameInput onSubmit={(name) => advance(name, { name })} />;
    if (step === "email") return <FreeText label="E-Mail" type="email" placeholder="ihre.email@beispiel.de" onSubmit={(v) => advance(v, { email: v })} />;
    if (step === "company_object")
      return <ContactInputs onSubmit={(p) => advance(p.company || p.object_type || "—", p)} mode="company_object" />;
    if (step === "confirm") return <ConfirmCard data={data} onSubmit={submitFlow} flow={flow} />;
  }
  // Shop set -------------------------------------------------------------
  if (flow === "shop_set") {
    if (step === "set_name") return <FreeText label="Welcher Raum / welches Set?" placeholder="z. B. Wohnzimmer · Skandi" onSubmit={(v) => advance(v, { set_name: v })} />;
    if (step === "usage_type") return <ChoiceChips items={SHOP_USAGE_TYPES} field="usage_type" advance={advance} columns={2} />;
    if (step === "quantity_region")
      return <ContactInputs onSubmit={(p) => advance(`${p.quantity} Einheit(en) · ${p.region}`, p)} mode="quantity_region" />;
    if (step === "timeline") return <FreeText label="Gewünschter Zeitraum" placeholder="z. B. Q2 2026" onSubmit={(v) => advance(v, { timeline: v })} />;
    if (step === "name") return <NameInput onSubmit={(name) => advance(name, { name })} />;
    if (step === "contact")
      return <ContactInputs onSubmit={(p) => advance(`${p.email}${p.phone ? " · " + p.phone : ""}`, p)} />;
    if (step === "confirm") return <ConfirmCard data={data} onSubmit={submitFlow} flow={flow} />;
  }
  // Project --------------------------------------------------------------
  if (flow === "project") {
    if (step === "object_type") return <ChoiceChips items={PROJECT_OBJECT_TYPES} field="object_type" advance={advance} />;
    if (step === "project_scope") return <ChoiceChips items={PROJECT_SCOPES} field="project_scope" advance={advance} />;
    if (step === "units") return <FreeText label="Anzahl Einheiten / Räume" type="number" placeholder="z. B. 6" onSubmit={(v) => advance(v, { number_of_units_or_rooms: v })} />;
    if (step === "style_series") return <ChoiceChips items={PROJECT_STYLES} field="style_series" advance={advance} />;
    if (step === "package_level") return <ChoiceChips items={PROJECT_PACKAGES} field="package_level" advance={advance} columns={4} />;
    if (step === "timeline") return <FreeText label="Gewünschter Zeitraum" placeholder="z. B. Q3 2026" onSubmit={(v) => advance(v, { timeline: v })} />;
    if (step === "name") return <NameInput onSubmit={(name) => advance(name, { name })} />;
    if (step === "contact")
      return <ContactInputs onSubmit={(p) => advance(`${p.email}${p.phone ? " · " + p.phone : ""}`, p)} extraField="message" extraLabel="Nachricht (optional)" />;
    if (step === "confirm") return <ConfirmCard data={data} onSubmit={submitFlow} flow={flow} />;
  }
  return null;
}

// ---------------------------------------------------------------------------
function DatePick({ onPick }) {
  const [d, setD] = useState();
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  return (
    <Calendar
      mode="single"
      selected={d}
      onSelect={(picked) => {
        setD(picked);
        if (picked) setTimeout(() => onPick(picked), 200);
      }}
      disabled={(day) => day < today || day.getDay() === 0 || day.getDay() === 6}
      weekStartsOn={1}
    />
  );
}

function NameInput({ onSubmit }) {
  return <FreeText label="Ihr Name" placeholder="Vor- und Nachname" onSubmit={onSubmit} />;
}

function FreeText({ label, placeholder, type = "text", onSubmit }) {
  const [v, setV] = useState("");
  return (
    <div className="space-y-2">
      <label className="overline">{label}</label>
      <div className="flex gap-2">
        <input
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && v.trim() && onSubmit(v.trim())}
          type={type}
          placeholder={placeholder}
          className="flex-1 bg-transparent border border-[color:var(--livinora-border)] focus:border-[color:var(--livinora-ink)] outline-none px-3 h-10 text-sm"
          data-testid={`nora-freetext-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
        />
        <button
          type="button"
          onClick={() => v.trim() && onSubmit(v.trim())}
          className="px-4 h-10 text-[11px] uppercase tracking-[0.16em] bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] disabled:opacity-50"
          disabled={!v.trim()}
        >
          Weiter
        </button>
      </div>
    </div>
  );
}

function ContactInputs({ onSubmit, requirePhone = false, mode, extraField, extraLabel }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [object_type, setObject] = useState("");
  const [quantity, setQuantity] = useState("");
  const [region, setRegion] = useState("");
  const [extra, setExtra] = useState("");
  const inputCls =
    "w-full bg-transparent border border-[color:var(--livinora-border)] focus:border-[color:var(--livinora-ink)] outline-none px-3 h-10 text-sm";
  const btn =
    "px-4 h-10 text-[11px] uppercase tracking-[0.16em] bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] disabled:opacity-50";

  if (mode === "company_object") {
    return (
      <div className="space-y-3">
        <label className="overline">Unternehmen (optional) · Objektart (optional)</label>
        <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Unternehmen" className={inputCls} />
        <input value={object_type} onChange={(e) => setObject(e.target.value)} placeholder="Objektart" className={inputCls} />
        <button type="button" onClick={() => onSubmit({ company, object_type })} className={btn}>
          Weiter
        </button>
      </div>
    );
  }
  if (mode === "quantity_region") {
    return (
      <div className="space-y-3">
        <label className="overline">Anzahl Einheiten · Lieferregion</label>
        <input value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="z. B. 8" className={inputCls} />
        <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region / Standort" className={inputCls} />
        <button type="button" onClick={() => quantity && region && onSubmit({ quantity, region })} disabled={!quantity || !region} className={btn}>
          Weiter
        </button>
      </div>
    );
  }
  const ok = email.includes("@") && (!requirePhone || phone.length >= 4);
  return (
    <div className="space-y-3">
      <label className="overline">E-Mail{requirePhone ? " · Telefon" : " · Telefon (optional)"}</label>
      <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="ihre.email@beispiel.de" className={inputCls} data-testid="nora-contact-email" />
      <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={requirePhone ? "Telefon" : "Telefon (optional)"} className={inputCls} data-testid="nora-contact-phone" />
      {extraField && (
        <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder={extraLabel || extraField} className={inputCls} />
      )}
      <button
        type="button"
        onClick={() => ok && onSubmit({ email, phone: phone || null, ...(extraField ? { [extraField]: extra || null } : {}) })}
        disabled={!ok}
        data-testid="nora-contact-submit"
        className={btn}
      >
        Weiter
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ConfirmCard({ data, flow, onSubmit }) {
  const summary = useMemo(() => buildSummary(flow, data), [flow, data]);
  return (
    <div className="hairline border bg-[color:var(--livinora-paper-warm)] p-5 space-y-4" data-testid="nora-confirm">
      <div className="overline">Zusammenfassung</div>
      <dl className="text-sm space-y-2">
        {summary.map(([k, v]) => (
          <div key={k} className="grid grid-cols-3 gap-3">
            <dt className="col-span-1 text-[10px] uppercase tracking-[0.16em] text-[color:var(--livinora-muted)] pt-0.5">{k}</dt>
            <dd className="col-span-2 text-[color:var(--livinora-ink)]">{v || "—"}</dd>
          </div>
        ))}
      </dl>
      <button type="button" onClick={onSubmit} data-testid="nora-confirm-submit" className="btn-livinora btn-primary w-full justify-center">
        Anfrage absenden <span>→</span>
      </button>
    </div>
  );
}

function buildSummary(flow, d) {
  if (flow === "appointment")
    return [["Thema", d.topic], ["Datum", d.selected_date ? fmtDate(d.selected_date) : ""], ["Uhrzeit", d.selected_time], ["Name", d.name], ["E-Mail", d.email], ["Telefon", d.phone], ["Anliegen", d.object_type]];
  if (flow === "callback")
    return [["Wunschzeit", d.preferred_time], ["Name", d.name], ["Telefon", d.phone], ["E-Mail", d.email], ["Anliegen", d.topic]];
  if (flow === "brochure")
    return [["Unterlagen", d.brochure_type], ["Name", d.name], ["E-Mail", d.email], ["Unternehmen", d.company], ["Objektart", d.object_type]];
  if (flow === "shop_set")
    return [["Set / Raum", d.set_name], ["Nutzung", d.usage_type], ["Anzahl", d.quantity], ["Region", d.region], ["Zeitraum", d.timeline], ["Name", d.name], ["E-Mail", d.email], ["Telefon", d.phone]];
  if (flow === "project")
    return [["Objektart", d.object_type], ["Vorhaben", d.project_scope], ["Einheiten", d.number_of_units_or_rooms], ["Stilserie", d.style_series], ["Paket", d.package_level], ["Zeitraum", d.timeline], ["Name", d.name], ["E-Mail", d.email], ["Telefon", d.phone], ["Nachricht", d.message]];
  return [];
}

// ---------------------------------------------------------------------------
function SuccessCard({ submitted, data, restart, navigate }) {
  const titleByType = {
    appointment_request: "Terminanfrage vorgemerkt",
    callback_request: "Rückrufbitte vorgemerkt",
    brochure_request: "Anfrage vorgemerkt",
    shop_set_request: "Raumset-Anfrage vorbereitet",
    project_request: "Projektanfrage vorbereitet",
  };
  const textByType = {
    appointment_request:
      "Vielen Dank. Ihre Angaben wurden aufgenommen. Das Livinora Team prüft Ihre Anfrage und bestätigt den Termin persönlich.",
    callback_request:
      "Vielen Dank. Ihre Rückrufbitte ist aufgenommen. Das Livinora Team meldet sich persönlich bei Ihnen.",
    brochure_request:
      "Vielen Dank. Wir senden Ihnen die passenden Unterlagen persönlich zu.",
    shop_set_request:
      "Vielen Dank. Ihre Raumset-Anfrage ist beim Livinora Team. Wir bestätigen Verfügbarkeit, Richtpreis und Lieferung im persönlichen Angebot.",
    project_request:
      "Vielen Dank. Ihre Projektanfrage ist beim Livinora Team. Wir melden uns persönlich, um die nächsten Schritte abzustimmen.",
  };
  const summary = useMemo(() => buildSummary(submitted.type.replace("_request", ""), data), [submitted, data]);

  // Context-specific extra CTA next to "Weitere Anfrage / Zurück"
  const extraCTA =
    submitted.type === "shop_set_request" ? { label: "Zum Shop", to: "/shop" } :
    submitted.type === "project_request" ? { label: "Zum Konfigurator", to: "/konfigurator" } :
    null;

  return (
    <div className="hairline border bg-[color:var(--livinora-paper-warm)] p-6 space-y-5" data-testid="nora-success">
      <div>
        <span className="overline">Erhalten</span>
        <h3 className="serif text-2xl mt-2 leading-snug">{titleByType[submitted.type] || "Anfrage vorgemerkt"}</h3>
        <p className="mt-3 text-sm text-[color:var(--livinora-muted)] leading-relaxed">
          {textByType[submitted.type]}
        </p>
      </div>
      {summary.length > 0 && (
        <dl className="text-sm border-t border-[color:var(--livinora-border)] pt-4 space-y-1.5">
          {summary.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-3">
              <dt className="col-span-1 text-[10px] uppercase tracking-[0.16em] text-[color:var(--livinora-muted)] pt-0.5">{k}</dt>
              <dd className="col-span-2 text-[color:var(--livinora-ink)]">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={restart}
          data-testid="nora-success-restart"
          className="text-[11px] uppercase tracking-[0.16em] px-3 py-2 min-h-[40px] border border-[color:var(--livinora-ink)] text-[color:var(--livinora-ink)] hover:bg-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-paper)] transition-colors"
        >
          Weitere Anfrage starten
        </button>
        {extraCTA && (
          <button
            type="button"
            onClick={() => navigate(extraCTA.to)}
            className="text-[11px] uppercase tracking-[0.16em] px-3 py-2 min-h-[40px] border border-[color:var(--livinora-ink)] text-[color:var(--livinora-ink)] hover:bg-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-paper)] transition-colors"
          >
            {extraCTA.label}
          </button>
        )}
        <Link
          to="/"
          className="text-[11px] uppercase tracking-[0.16em] px-3 py-2 min-h-[40px] border border-[color:var(--livinora-border)] text-[color:var(--livinora-muted)] hover:border-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-ink)] transition-colors"
        >
          Zur Website zurück
        </Link>
      </div>
    </div>
  );
}
